import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronRight, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { partnersQuoteRequest } from 'rails_routes'
import { Button, ButtonGroup, IconButton, Modal } from 'slab'
import { useDeleteQuotingProspectsId, useGetQuotingProspects } from 'utils/web_api'
import { type Pagination, type QuotingProspect, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'

import RequestAQuoteButton from './RequestAQuoteButton'
import { getLocaleDateString } from '../../utils'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<QuotingProspect>()

type PartnersQuotesListProps = {
  currentUserId?: string;
  currentPartnerId?: string;
  canOwnProspects?: boolean;
  pageParam?: number | null;
  useMultiStepQuoteRequestFlow?: boolean
}

export function PartnersQuotesListImpl ({
  currentUserId,
  currentPartnerId,
  canOwnProspects,
  pageParam,
  useMultiStepQuoteRequestFlow = false,
}: PartnersQuotesListProps) {
  const [prospectToDelete, setProspectToDelete] = useState<QuotingProspect>()
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [paginationMetadata, setPaginationMetadata] = useState<
    Pagination | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, isError, refetch } = useGetQuotingProspects({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    ...(currentUserId && { owner_id: currentUserId }),
    ...(currentPartnerId && { partner_id: currentPartnerId }),
  })

  const { mutate: deleteProspect } = useDeleteQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        setDeleteModalOpen(false)
        refetch()
      },
      onError: () => {
        toast.error('Failed to delete draft prospect')
      }
    }
  })

  const deleteCurrentProspect = () => prospectToDelete && deleteProspect({ id: prospectToDelete.id })

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => info.getValue() ?? <span className='text-gray-400'>(no name)</span>
    }),
    columnHelper.accessor('email', {
      header: 'Email',
    }),
    columnHelper.accessor('created_at', {
      header: 'Date created',
      cell: (info) =>
        info.getValue()
          ? getLocaleDateString(new Date(info.getValue()))
          : undefined,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { quote_request_status: quoteRequestStatus } = row.original
        const isDraft = quoteRequestStatus !== QuotingProspectQuoteRequestStatus.complete
        return (
          isDraft
            ? (
              <div className='flex gap-4 items-center justify-end whitespace-nowrap'>
                <IconButton
                  icon={Trash2} label='Delete' onClick={(event) => {
                    event.stopPropagation()

                    setProspectToDelete(row.original)
                    setDeleteModalOpen(true)
                  }}
                />
                {useMultiStepQuoteRequestFlow && <ChevronRight size={20} className='stroke-gray-500' />}
              </div>)
            : null
        )
      },
    }),
  ], [useMultiStepQuoteRequestFlow])

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        name: true,
        email: true,
        created_at: true,
      },
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  useEffect(() => {
    const pageURL = new URL(window.location.href)
    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)
    window.history.pushState({}, '', pageURL.toString())
  }, [pagination.pageIndex])

  useEffect(() => {
    // `quote_submitted` is only used to flash a success message from QuotesController
    // so we remove it from the URL after it's detected
    const url = new URL(window.location.href)
    url.searchParams.delete('quote_submitted')
    window.history.replaceState({}, document.title, url)
  }, [])

  return (
    <>
      <div className='flex flex-row items-start justify-between flex-wrap gap-3 mb-6'>
        <h1 className='heading-lg'>Quotes</h1>
        {canOwnProspects && (
          <RequestAQuoteButton
            location='top_nav'
            useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
          />
        )}
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage={
          <div className='flex flex-col items-center justify-center gap-4'>
            You haven't requested any quotes yet.
            {canOwnProspects && (
              <RequestAQuoteButton
                isPrimary={false}
                location='quotes_zero_state'
                useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
              />
            )}
          </div>
        }
        onRowClick={({ row }) => {
          if (useMultiStepQuoteRequestFlow && row.original.quote_request_status !== QuotingProspectQuoteRequestStatus.complete) {
            window.location.href = partnersQuoteRequest.show({ id: row.original.id })
          }
        }}
        isThisRowClicklable={(row) => useMultiStepQuoteRequestFlow && row.original.quote_request_status !== QuotingProspectQuoteRequestStatus.complete}
        paginationLabelSingular='quote'
        paginationLabelPlural='quotes'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />

      <Modal open={isDeleteModalOpen} onOpenChange={setDeleteModalOpen} aria-describedby={undefined}>
        <Modal.Header>
          <Modal.Title>Delete {prospectToDelete?.name ?? 'prospect'}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This action can't be undone. All progress associated with {prospectToDelete?.name ?? 'this prospect'}, including information entered and files uploaded, will be deleted.</p>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup align='right'>
            <Button
              variant='secondary'
              size='large'
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='primary-destructive'
              size='large'
              leftIcon={Trash2}
              onClick={deleteCurrentProspect}
            >
              Delete
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const PartnersQuotesList = ({
  currentUserId,
  currentPartnerId,
  canOwnProspects,
  pageParam,
  useMultiStepQuoteRequestFlow = false,
}: PartnersQuotesListProps) => {
  return (
    <PartnersQuotesListImpl
      currentUserId={currentUserId}
      currentPartnerId={currentPartnerId}
      canOwnProspects={canOwnProspects}
      pageParam={pageParam}
      useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
    />
  )
}

export default PartnersQuotesList
